import React, { Component, useState, useEffect } from 'react';
import { Table, Divider, Button, Empty, PageHeader, BackTop, Badge, Spin, Row, Col } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';

import styles from './NotificationTable.module.scss';

const { Column } = Table;

const FILTER_FIELDS = [
  { label: 'ID', value: 'id' },
  { label: 'Request ID', value: 'request_id' },
  { label: 'Days Left', value: 'days_left' },
  { label: 'Status', value: 'status' },
  { label: 'Created At', value: 'created_at' }
];

const FILTER_OPERATORS = [
  { label: 'equals', value: 'eq' },
  { label: 'not equals', value: 'neq' },
  { label: 'contains', value: 'like' },
  { label: 'not contains', value: 'notlike' },
  { label: 'greater than', value: 'gt' },
  { label: 'greater than or equals', value: 'gte' },
  { label: 'less than', value: 'lt' },
  { label: 'less than or equals', value: 'lte' }
];

function FilterBar({ filters, setFilters, onApply }) {
  const handleFieldChange = (idx, value) => {
    const newFilters = [...filters];
    newFilters[idx].field = value;
    setFilters(newFilters);
  };
  const handleOperatorChange = (idx, value) => {
    const newFilters = [...filters];
    newFilters[idx].operator = value;
    setFilters(newFilters);
  };
  const handleValueChange = (idx, value) => {
    const newFilters = [...filters];
    newFilters[idx].value = value;
    setFilters(newFilters);
  };
  const handleRemove = idx => {
    const newFilters = filters.filter((_, i) => i !== idx);
    setFilters(newFilters);
    // If all filters are removed, automatically apply empty filters
    if (newFilters.length === 0) {
      onApply([]);
    }
  };
  const handleAdd = () => {
    setFilters([...filters, { field: '', operator: 'eq', value: '' }]);
  };
  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        background: '#fafafa',
        padding: 16
      }}
    >
      {filters.map((filter, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 8,
            alignItems: 'center',
            background: '#fff',
            border: '1px solid #e4e4e4',
            borderRadius: 4,
            padding: '8px 12px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}
        >
          <select
            value={filter.field}
            onChange={e => handleFieldChange(idx, e.target.value)}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '4px 8px',
              background: '#fafafa',
              minWidth: 120
            }}
          >
            <option value="">Field</option>
            {FILTER_FIELDS.map(f => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <select
            value={filter.operator}
            onChange={e => handleOperatorChange(idx, e.target.value)}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '4px 8px',
              background: '#fafafa',
              minWidth: 120
            }}
          >
            {FILTER_OPERATORS.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          <input
            value={filter.value}
            onChange={e => handleValueChange(idx, e.target.value)}
            placeholder="Enter value..."
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '4px 8px',
              background: '#fafafa',
              minWidth: 140
            }}
          />
          <Button onClick={() => handleRemove(idx)} size="small">
            X
          </Button>
        </div>
      ))}
      <Button onClick={handleAdd} size="small" type="default" style={{ marginRight: 8 }}>
        + Add filter
      </Button>
      {filters.length > 0 && (
        <Button type="primary" onClick={() => onApply(filters)} style={{ marginRight: 8 }}>
          Apply filters
        </Button>
      )}
    </div>
  );
}

class NotificationTable extends Component {
  // Map for all API field names
  fieldNameMap = {
    ID: 'id',
    RequestID: 'request_id',
    DaysLeft: 'days_left',
    Status: 'status',
    CreatedAt: 'created_at'
  };

  handleTableChange = (pagination, _filters, sorter) => {
    const { fetchNotifications, sort, filter } = this.props;
    let sortStr;
    if (sorter && sorter.field) {
      const field = this.fieldNameMap[sorter.field] || sorter.field;
      sortStr = `${field}:${sorter.order === 'descend' ? 'desc' : 'asc'}`;
    } else {
      sortStr = sort;
    }
    fetchNotifications(pagination.current, pagination.pageSize, sortStr, filter);
  };

  renderStatus = text => (
    <span>
      {text.toLowerCase() === 'success' ? (
        <Badge color="green" text={text} />
      ) : (
        <Badge color="red" text={text} />
      )}
    </span>
  );

  render() {
    let { data, total, page, pageSize, sort } = this.props;

    // Parse current sort field and order from sort string
    let currentSortField, currentSortOrder;
    if (sort) {
      const [field, order] = sort.split(':');
      currentSortField =
        Object.keys(this.fieldNameMap).find(k => this.fieldNameMap[k] === field) || field;
      currentSortOrder = order === 'desc' ? 'descend' : 'ascend';
    }

    const tablePagination = {
      current: page,
      pageSize: pageSize,
      total: total,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    };

    return data.length !== 0 ? (
      <Table
        dataSource={data}
        pagination={tablePagination}
        rowKey={record => record.ID}
        onChange={this.handleTableChange}
      >
        <Column
          title="ID"
          dataIndex="ID"
          sorter={true}
          sortOrder={currentSortField === 'ID' ? currentSortOrder : undefined}
        />
        <Column
          title="Request ID"
          dataIndex="RequestID"
          sorter={true}
          sortOrder={currentSortField === 'RequestID' ? currentSortOrder : undefined}
        />
        <Column
          title="Days Left"
          dataIndex="DaysLeft"
          sorter={true}
          sortOrder={currentSortField === 'DaysLeft' ? currentSortOrder : undefined}
        />
        <Column
          title="Customer Email"
          dataIndex={['NotifyInfo', 'CustomerEmails']}
          render={emails =>
            emails && emails.length > 0
              ? emails.map((email, idx) => <div key={idx}>{email}</div>)
              : '-'
          }
          align="left"
        />
        <Column
          title="CC"
          dataIndex={['NotifyInfo', 'CC']}
          render={emails =>
            emails && emails.length > 0
              ? emails.map((email, idx) => <div key={idx}>{email}</div>)
              : '-'
          }
          align="left"
        />
        <Column
          title="Status"
          dataIndex="Status"
          sorter={true}
          sortOrder={currentSortField === 'Status' ? currentSortOrder : undefined}
          render={this.renderStatus}
        />
        <Column title="Error Message" dataIndex="ErrorMessage" />
        <Column
          title="Created At"
          dataIndex="CreatedAt"
          sorter={true}
          sortOrder={currentSortField === 'CreatedAt' ? currentSortOrder : undefined}
          render={text => moment(text).format('YYYY-MM-DD HH:mm:ss')}
        />
      </Table>
    ) : (
      <Empty />
    );
  }
}

NotificationTable.propTypes = {
  data: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  fetchNotifications: PropTypes.func.isRequired
};

const NotificationPage = ({
  data,
  isFetching,
  fetchNotifications,
  total,
  page,
  pageSize,
  sort,
  filter,
  accessToken
}) => {
  const [filters, setFilters] = useState([]);
  useEffect(() => {
    if (accessToken) {
      fetchNotifications(1, pageSize);
    }
  }, [accessToken, pageSize, fetchNotifications]);

  const handleApplyFilters = filtersArr => {
    // If no filters, show original list
    if (!filtersArr.length) {
      setFilters([]);
      fetchNotifications(1, pageSize, undefined, {}); // Explicitly reset filter and sort
      return;
    }
    // Convert filters array to API filter object
    const filterObj = {};
    filtersArr.forEach(f => {
      if (f.field && f.operator && f.value) {
        filterObj[`${f.field}:${f.operator}`] = f.value;
      }
    });
    fetchNotifications(1, pageSize, sort, filterObj);
  };

  return (
    <>
      <FilterBar filters={filters} setFilters={setFilters} onApply={handleApplyFilters} />
      <PageHeader
        title="Notification History"
        extra={[
          <Row type="flex" justify="space-between" align="top" key={0}>
            {data.length !== 0 && (
              <Col>
                <Button onClick={() => fetchNotifications(page, pageSize, sort, filter)}>
                  Refresh
                </Button>
              </Col>
            )}
          </Row>
        ]}
      >
        <Divider type={'horizontal'} />

        {isFetching ? (
          <div className={styles.spinContainer}>
            <Spin size={'large'} tip={'Fetching notification history...'} />
          </div>
        ) : (
          <NotificationTable
            data={data}
            fetchNotifications={fetchNotifications}
            total={total}
            page={page}
            pageSize={pageSize}
            sort={sort}
            filter={filter}
          />
        )}

        <BackTop visibilityHeight={100} />
      </PageHeader>
    </>
  );
};

NotificationPage.propTypes = {
  data: PropTypes.array.isRequired,
  isFetching: PropTypes.bool.isRequired,
  fetchNotifications: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sort: PropTypes.string,
  filter: PropTypes.object,
  accessToken: PropTypes.string
};

export default NotificationPage;
